if (!customElements.get('armada-video')) {
  /**
   * Where every video in this section starts once the shopper unmutes it.
   * Full blast is a jump-scare on a product page, and the browser's own default
   * of 1 is what the slider used to inherit; half gives the volume control
   * somewhere to go in both directions.
   */
  const DEFAULT_VOLUME = 0.5;

  /**
   * Swap the `data-poster` / `data-src` placeholders the section renders for the
   * real attributes. The Video section sits below the fold on every PDP, so
   * nothing about it — not even the poster frame — is fetched until it nears the
   * viewport. Idempotent: safe to call from the observer, from play() and from a
   * breakpoint change without re-triggering a load.
   */
  const promoteMedia = (video) => {
    if (!video || video.dataset.lazyLoaded === 'true') return;
    video.dataset.lazyLoaded = 'true';

    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      video.removeAttribute('data-poster');
    }

    let needsLoad = false;
    if (video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
      needsLoad = true;
    }
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      needsLoad = true;
    });

    // A <source> list is only re-read after an explicit load(). With
    // preload="none" this prepares the element without pulling the file.
    if (needsLoad) video.load();
  };

  class ArmadaVideo extends HTMLElement {
    constructor() {
      super();
      this.state = 'initial';
      this.isDragging = false;
      this.hideTimer = null;
      this.isYouTube = false;
      this.lazyReady = false;
      // Set when the shopper pauses by hand, so an autoplaying video that
      // scrolls away and back doesn't override that choice.
      this.userPaused = false;
      this.enableAudio = false;
      this.userUnmuted = false;
      this.lastVolume = DEFAULT_VOLUME;
      // AAS-5595: only playback the shopper asked for is announced to the page
      // (see _announcePlayback), so an autoplaying background loop never hides
      // the sticky add-to-cart bar.
      this.userInitiated = false;
      this.announcedPlaying = false;
      this.dragTarget = null;
      this._onFullscreenChange = () => this.syncFullscreenUI();
      this._onDocMouseMove = (e) => this.onDrag(e);
      this._onDocTouchMove = (e) => this.onDrag(e);
      this._onDocMouseUp = () => this.endDrag();
      this._onDocTouchEnd = () => this.endDrag();
    }

    connectedCallback() {
      this.playButton = this.querySelector('.armada-video__play-button');
      this.youtubeUrl = this.dataset.youtubeUrl;
      this.isYouTube = !!this.youtubeUrl;

      if (this.isYouTube) {
        this._initYouTube();
      } else {
        this._initShopifyVideo();
      }
    }

    disconnectedCallback() {
      // The variant sync replaces this whole section while it may still be
      // playing. A dispatch from a detached node cannot bubble to the page, so
      // the listeners prune elements that have left the DOM instead; this only
      // has to make sure the element stops claiming to be playing.
      this.announcedPlaying = false;

      document.removeEventListener('fullscreenchange', this._onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this._onFullscreenChange);
      document.removeEventListener('mousemove', this._onDocMouseMove);
      document.removeEventListener('touchmove', this._onDocTouchMove);
      document.removeEventListener('mouseup', this._onDocMouseUp);
      document.removeEventListener('touchend', this._onDocTouchEnd);

      if (this.videoAC) this.videoAC.abort();
      if (this.lazyIO) this.lazyIO.disconnect();
      if (this.autoplayIO) this.autoplayIO.disconnect();
      this.clearHideTimer();
    }

    /* ======================================================================
       YouTube mode
       ====================================================================== */

    _initYouTube() {
      this.youtubeTarget = this.querySelector('[data-youtube-target]');
      this.playButton.addEventListener('click', () => this._playYouTube());
    }

    _playYouTube() {
      const videoId = this._parseYouTubeId(this.youtubeUrl);
      if (!videoId || !this.youtubeTarget) return;

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.title = this.getAttribute('aria-label') || 'YouTube video';

      this.youtubeTarget.appendChild(iframe);
      this.dataset.state = 'playing';
      this.state = 'playing';
      // No pause signal is available without pulling in the YouTube iframe API,
      // which is exactly the weight the lazy embed avoids. The listeners
      // release the hold when the section scrolls out of view instead.
      this._announcePlayback(true);
    }

    _parseYouTubeId(url) {
      if (!url) return null;
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
        /^([a-zA-Z0-9_-]{11})$/
      ];
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
      return null;
    }

    /* ======================================================================
       Shopify video mode
       ====================================================================== */

    _initShopifyVideo() {
      this.videoDesktop = this.querySelector('.armada-video__media--desktop');
      this.videoMobile = this.querySelector('.armada-video__media--mobile');
      this.video = this.getActiveVideo();
      this.controls = this.querySelector('.armada-video__controls');
      this.controlsPlayPause = this.querySelector('.armada-video__controls-playpause');
      this.progressContainer = this.querySelector('.armada-video__progress-container');
      this.progressFill = this.querySelector('.armada-video__progress-fill');
      this.iconPause = this.querySelector('.armada-video__icon-pause');
      this.iconPlay = this.querySelector('.armada-video__icon-play');
      this.muteButton = this.querySelector('.armada-video__mute');
      this.volumeSlider = this.querySelector('.armada-video__volume-slider');
      this.volumeFill = this.querySelector('.armada-video__volume-fill');
      this.iconSpeaker = this.querySelector('.armada-video__icon-speaker');
      this.iconSpeakerMute = this.querySelector('.armada-video__icon-speaker-mute');
      this.fullscreenButton = this.querySelector('.armada-video__fullscreen');
      this.iconExpand = this.querySelector('.armada-video__icon-expand');
      this.iconCollapse = this.querySelector('.armada-video__icon-collapse');
      this.enableAudio = this.dataset.enableAudio === 'true';

      if (!this.video) return;

      // Set on the element rather than only tracked in lastVolume, so it holds
      // for the mute button, the slider and the keyboard alike.
      this.video.volume = DEFAULT_VOLUME;
      if (!this.enableAudio) this.video.muted = true;

      this.mql = window.matchMedia('(max-width: 749px)');
      this.mql.addEventListener('change', () => this.onBreakpointChange());

      this.bindEvents();
      this.syncVolumeUI();
      this._initFullscreen();
      this._initLazyMedia();
      this._initAutoplay();
    }

    /* Lazy media
       ====================================================================== */

    /**
     * Promote the active video's placeholders once the section gets within a
     * screenful or so of the viewport. Only the ACTIVE cut is promoted — on a
     * desktop/mobile pair the hidden one never fetches its poster at all.
     */
    _initLazyMedia() {
      const activate = () => {
        this.lazyReady = true;
        promoteMedia(this.video);
      };

      if (!('IntersectionObserver' in window)) {
        activate();
        return;
      }

      this.lazyIO = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.lazyIO.disconnect();
          this.lazyIO = null;
          activate();
        },
        { rootMargin: '600px 0px' }
      );
      this.lazyIO.observe(this);
    }

    /* Autoplay
       ====================================================================== */

    /**
     * Opt-in via the section's Autoplay setting. Deliberately narrow:
     *   - only for <video> (YouTube would mean loading the iframe embed on
     *     scroll, which is exactly the weight lazy loading is avoiding),
     *   - muted, because browsers refuse unmuted autoplay,
     *   - never under prefers-reduced-motion,
     *   - paused again on the way out, so an off-screen video isn't burning
     *     CPU and battery.
     */
    _initAutoplay() {
      if (this.dataset.autoplay !== 'true') return;
      if (!('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.autoplayIO = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!this.video) return;

            if (entry.isIntersecting) {
              if (this.userPaused) return;
              promoteMedia(this.video);
              if (!this.enableAudio || !this.userUnmuted) {
                this.video.muted = true;
              }
              this.syncVolumeUI();
              this.userInitiated = false;
              const played = this.video.play();
              // Still refused (low power mode, platform policy) — leave the
              // poster and the play button in place rather than throwing.
              if (played && played.catch) played.catch(() => {});
            } else if (!this.video.paused) {
              this.video.pause();
            }
          });
        },
        { threshold: 0.25 }
      );
      this.autoplayIO.observe(this);
    }

    getActiveVideo() {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (isMobile) {
        return this.videoMobile || this.videoDesktop || this.querySelector('video');
      }
      return this.videoDesktop || this.videoMobile || this.querySelector('video');
    }

    isMobile() {
      return this.mql ? this.mql.matches : window.matchMedia('(max-width: 749px)').matches;
    }

    onBreakpointChange() {
      const newVideo = this.getActiveVideo();
      if (newVideo !== this.video) {
        // Handing playback from one cut to the other is a pause and a play in
        // the same task. It does not blink the announcement: media events are
        // queued, and bindVideoEvents() aborts the old cut's listeners before
        // its `pause` is ever delivered, so the only event that lands is the
        // new cut's `play` — which _announcePlayback already treats as a no-op.
        const wasPlaying = this.video && !this.video.paused;
        const currentTime = this.video ? this.video.currentTime : 0;
        const muted = this.video ? this.video.muted : true;
        const volume = this.video ? this.video.volume : 1;

        if (wasPlaying) this.video.pause();

        this.video = newVideo;
        if (!this.video) return;

        // The other cut was left un-promoted on purpose; it needs its source now
        // that it is the visible one.
        if (this.lazyReady) promoteMedia(this.video);

        this.video.currentTime = currentTime;
        this.video.volume = volume;
        this.video.muted = this.enableAudio ? muted : true;
        this.bindVideoEvents();

        if (wasPlaying) this.video.play();
      }

      this.syncVolumeUI();
    }

    /* Event binding
       ====================================================================== */

    bindEvents() {
      this.playButton.addEventListener('click', () => this.play());
      this.controlsPlayPause.addEventListener('click', () => this.togglePlay());

      this.bindVideoEvents();

      this.progressContainer.addEventListener('click', (e) => this.seek(e));
      this.progressContainer.addEventListener('mousedown', (e) => this.startDrag(e, 'progress'));
      this.progressContainer.addEventListener('touchstart', (e) => this.startDrag(e, 'progress'), { passive: false });
      document.addEventListener('mousemove', this._onDocMouseMove);
      document.addEventListener('touchmove', this._onDocTouchMove, { passive: false });
      document.addEventListener('mouseup', this._onDocMouseUp);
      document.addEventListener('touchend', this._onDocTouchEnd);

      this.progressContainer.addEventListener('keydown', (e) => this.onProgressKeydown(e));

      if (this.enableAudio && this.muteButton && this.volumeSlider) {
        this.muteButton.addEventListener('click', () => this.toggleMute());
        this.volumeSlider.addEventListener('click', (e) => this.seekVolume(e));
        this.volumeSlider.addEventListener('mousedown', (e) => this.startDrag(e, 'volume'));
        this.volumeSlider.addEventListener('touchstart', (e) => this.startDrag(e, 'volume'), { passive: false });
        this.volumeSlider.addEventListener('keydown', (e) => this.onVolumeKeydown(e));
      }

      this.addEventListener('mousemove', () => {
        if (this.isMobile()) return;
        this.resetHideTimer();
      });
      this.addEventListener('mouseleave', () => {
        if (this.isMobile()) return;
        this.startHideTimer();
      });
    }

    bindVideoEvents() {
      if (this.videoAC) this.videoAC.abort();
      this.videoAC = new AbortController();
      const opts = { signal: this.videoAC.signal };

      this.video.addEventListener('click', () => {
        if (this.state === 'initial') {
          this.play();
          return;
        }
        if (this.isMobile()) {
          this.toggleControlsVisibility();
          return;
        }
        this.togglePlay();
      }, opts);
      this.video.addEventListener('play', () => this.onPlay(), opts);
      this.video.addEventListener('pause', () => this.onPause(), opts);
      this.video.addEventListener('ended', () => this.onEnded(), opts);
      this.video.addEventListener('timeupdate', () => {
        if (!this.isDragging) this.updateProgress();
      }, opts);
      this.video.addEventListener('volumechange', () => this.syncVolumeUI(), opts);
    }

    /* State management
       ====================================================================== */

    onPlay() {
      this.dataset.state = 'playing';
      this.state = 'playing';
      this.iconPause.classList.remove('hidden');
      this.iconPlay.classList.add('hidden');
      this.setPlayPauseLabel(true);
      this.startHideTimer();
      if (this.userInitiated) this._announcePlayback(true);
    }

    onPause() {
      this.dataset.state = 'paused';
      this.state = 'paused';
      this.iconPause.classList.add('hidden');
      this.iconPlay.classList.remove('hidden');
      this.setPlayPauseLabel(false);
      this.clearHideTimer();
      this.classList.remove('armada-video--controls-hidden');
      this._announcePlayback(false);
    }

    onEnded() {
      this.dataset.state = 'initial';
      this.state = 'initial';
      this.video.currentTime = 0;
      this.updateProgress();
      this.setPlayPauseLabel(false);
      this.clearHideTimer();
      this.classList.remove('armada-video--controls-hidden');
      this._announcePlayback(false);
    }

    /**
     * AAS-5595: tell the rest of the page that a shopper is watching.
     *
     * `media:play` / `media:pause` are not invented here — deferred-media (the
     * PDP gallery) has emitted this pair since the theme's Dawn ancestry, so
     * anything that already listens picks this player up for free. The sticky
     * add-to-cart bar is the first such listener: it sits fixed to the bottom
     * of the viewport, directly over this section's own controls, and now gets
     * out of the way while the video is on screen and playing.
     *
     * Autoplay is deliberately silent. A muted background loop is decoration,
     * not something the shopper chose to watch, and hiding the buy button for
     * it would cost conversions for nothing. Only the paths a person can
     * trigger — play(), the play button, the poster click, the YouTube embed —
     * set `userInitiated`.
     *
     * The pair is kept balanced by `announcedPlaying` so a repeated `play`
     * event (a source swap on resize, a seek that re-fires) cannot leave a
     * listener holding a stale claim.
     */
    _announcePlayback(playing) {
      if (this.announcedPlaying === playing) return;
      this.announcedPlaying = playing;
      this.dispatchEvent(
        new CustomEvent(playing ? 'media:play' : 'media:pause', { bubbles: true, detail: this })
      );
    }

    /* Transport
       ====================================================================== */

    setPlayPauseLabel(playing) {
      if (!this.controlsPlayPause) return;
      const { labelPause, labelPlay } = this.controlsPlayPause.dataset;
      const label = playing ? labelPause : labelPlay;
      if (label) this.controlsPlayPause.setAttribute('aria-label', label);
    }

    play() {
      // Covers a tap that beats the lazy observer — a deep link landing on the
      // section, or a fast scroll past the 600px margin.
      promoteMedia(this.video);
      this.userPaused = false;
      // Every caller of play() is a person: the poster play button, the click
      // on the video itself, and togglePlay() from the transport control. The
      // autoplay observer calls this.video.play() directly and stays silent.
      this.userInitiated = true;
      if (!this.enableAudio) this.video.muted = true;
      this.video.play();
    }

    pause() {
      this.video.pause();
    }

    togglePlay() {
      if (this.video.paused) {
        this.play();
      } else {
        this.userPaused = true;
        this.pause();
      }
    }

    /* Progress
       ====================================================================== */

    updateProgress() {
      if (!this.video.duration) return;
      const pct = (this.video.currentTime / this.video.duration) * 100;
      this.progressFill.style.width = `${pct}%`;
      this.progressContainer.setAttribute('aria-valuenow', `${Math.round(pct)}`);
    }

    seek(e) {
      if (this.isDragging) return;
      const pos = this.getProgressPosition(e);
      this.video.currentTime = pos * this.video.duration;
    }

    startDrag(e, target) {
      this.dragTarget = target || 'progress';
      this.isDragging = true;
      this.onDrag(e);
    }

    onDrag(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      if (this.dragTarget === 'volume') {
        this.applyVolume(this.getVolumePosition(e));
        return;
      }
      const pos = this.getProgressPosition(e);
      const pct = pos * 100;
      this.progressFill.style.width = `${pct}%`;
      this.progressContainer.setAttribute('aria-valuenow', `${Math.round(pct)}`);
      this.video.currentTime = pos * this.video.duration;
    }

    endDrag() {
      this.isDragging = false;
      this.dragTarget = null;
    }

    getProgressPosition(e) {
      const rect = this.progressContainer.getBoundingClientRect();
      const { touches, clientX } = e;
      const x = touches ? touches[0].clientX : clientX;
      return Math.max(0, Math.min(1, (x - rect.left) / rect.width));
    }

    onProgressKeydown(e) {
      if (!this.video.duration) return;
      const step = 5;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          this.video.currentTime = Math.min(this.video.duration, this.video.currentTime + step);
          this.updateProgress();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.video.currentTime = Math.max(0, this.video.currentTime - step);
          this.updateProgress();
          break;
      }
    }

    /* Volume
       ====================================================================== */

    isVerticalVolume() {
      return this.isMobile();
    }

    getVolumePosition(e) {
      const rect = this.volumeSlider.getBoundingClientRect();
      const { touches, clientX, clientY } = e;
      if (this.isVerticalVolume()) {
        const y = touches ? touches[0].clientY : clientY;
        return Math.max(0, Math.min(1, (rect.bottom - y) / rect.height));
      }
      const x = touches ? touches[0].clientX : clientX;
      return Math.max(0, Math.min(1, (x - rect.left) / rect.width));
    }

    seekVolume(e) {
      if (this.isDragging) return;
      this.applyVolume(this.getVolumePosition(e));
    }

    applyVolume(pos) {
      if (!this.video || !this.enableAudio) return;
      this.video.volume = pos;
      if (pos === 0) {
        this.video.muted = true;
      } else {
        this.lastVolume = pos;
        this.video.muted = false;
        this.userUnmuted = true;
      }
      this.syncVolumeUI();
    }

    toggleMute() {
      if (!this.video || !this.enableAudio) return;
      if (this.video.muted || this.video.volume === 0) {
        this.video.muted = false;
        this.video.volume = this.lastVolume || DEFAULT_VOLUME;
        this.userUnmuted = true;
      } else {
        this.lastVolume = this.video.volume || DEFAULT_VOLUME;
        this.video.muted = true;
      }
      this.syncVolumeUI();
    }

    syncVolumeUI() {
      if (!this.volumeFill || !this.volumeSlider) return;
      const muted = !this.video || this.video.muted || this.video.volume === 0;
      const level = muted ? 0 : this.video.volume;
      const pct = Math.round(level * 100);

      if (this.isVerticalVolume()) {
        this.volumeFill.style.height = `${pct}%`;
        this.volumeFill.style.width = '';
      } else {
        this.volumeFill.style.width = `${pct}%`;
        this.volumeFill.style.height = '';
      }
      this.volumeSlider.setAttribute('aria-valuenow', `${pct}`);
      this.volumeSlider.setAttribute(
        'aria-orientation',
        this.isVerticalVolume() ? 'vertical' : 'horizontal'
      );

      if (this.iconSpeaker && this.iconSpeakerMute) {
        this.iconSpeaker.classList.toggle('hidden', muted);
        this.iconSpeakerMute.classList.toggle('hidden', !muted);
      }
      if (this.muteButton) {
        const { labelUnmute, labelMute } = this.muteButton.dataset;
        const label = muted ? labelUnmute : labelMute;
        if (label) this.muteButton.setAttribute('aria-label', label);
      }
    }

    onVolumeKeydown(e) {
      if (!this.video) return;
      const step = 0.05;
      const vertical = this.isVerticalVolume();
      const current = this.video.muted ? 0 : this.video.volume;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          if (vertical && e.key === 'ArrowRight') return;
          if (!vertical && e.key === 'ArrowUp') return;
          e.preventDefault();
          this.applyVolume(Math.min(1, current + step));
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          if (vertical && e.key === 'ArrowLeft') return;
          if (!vertical && e.key === 'ArrowDown') return;
          e.preventDefault();
          this.applyVolume(Math.max(0, current - step));
          break;
      }
    }

    /* Fullscreen
       ======================================================================
       AAS-5595. Two things the PDP needed: a way out from under the sticky
       add-to-cart bar, and the control every other video player on the web has.
       The browser's top layer takes care of the first — a fixed-position
       element cannot paint over a fullscreen one, whatever its z-index.
       ====================================================================== */

    /**
     * Reveal the control only if the browser really offers a way to go
     * fullscreen, and wire the change listeners. Called once, from
     * _initShopifyVideo — the YouTube branch has the embed's own controls.
     */
    _initFullscreen() {
      if (!this.fullscreenButton) return;

      const canFullscreenElement = !!(this.requestFullscreen || this.webkitRequestFullscreen);
      const canFullscreenVideo = !!(
        this.video &&
        (this.video.webkitSetPresentationMode || this.video.webkitEnterFullscreen)
      );
      // A button that cannot do anything is worse than no button; iOS Safari
      // has no element fullscreen but does have the media one, so both count.
      if (!canFullscreenElement && !canFullscreenVideo) return;

      this.fullscreenButton.classList.remove('hidden');
      this.fullscreenButton.addEventListener('click', () => this.toggleFullscreen());
      document.addEventListener('fullscreenchange', this._onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this._onFullscreenChange);
    }

    isFullscreen() {
      const active = document.fullscreenElement || document.webkitFullscreenElement;
      return !!active && (active === this || this.contains(active));
    }

    toggleFullscreen() {
      if (this.isFullscreen()) {
        this.exitFullscreen();
      } else {
        this.enterFullscreen();
      }
    }

    /**
     * The whole component goes fullscreen, not the <video>. The heading,
     * progress bar, volume and the play/pause button are siblings of the media
     * element, so promoting the <video> alone would drop every one of them and
     * hand playback to the browser's own chrome.
     */
    enterFullscreen() {
      if (this.requestFullscreen) {
        const request = this.requestFullscreen();
        // Safari desktop resolves this, but a refusal (a nested fullscreen, a
        // permissions policy) should still get the shopper a fullscreen video.
        if (request && request.catch) request.catch(() => this._enterVideoFullscreen());
        return;
      }
      if (this.webkitRequestFullscreen) {
        this.webkitRequestFullscreen();
        return;
      }
      this._enterVideoFullscreen();
    }

    /**
     * Last resort: iOS Safari exposes fullscreen on the media element only, and
     * takes over with its native player. The custom controls are gone for the
     * duration, which is why this is the fallback rather than the default.
     */
    _enterVideoFullscreen() {
      if (!this.video) return;
      if (typeof this.video.webkitSetPresentationMode === 'function') {
        this.video.webkitSetPresentationMode('fullscreen');
        return;
      }
      if (typeof this.video.webkitEnterFullscreen === 'function') {
        this.video.webkitEnterFullscreen();
      }
    }

    exitFullscreen() {
      if (document.exitFullscreen) {
        const exit = document.exitFullscreen();
        if (exit && exit.catch) exit.catch(() => {});
        return;
      }
      if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }

    /**
     * Driven by the fullscreenchange event rather than by the click, so Escape
     * and the browser's own exit affordance keep the icon and the label honest.
     */
    syncFullscreenUI() {
      if (!this.fullscreenButton) return;
      const on = this.isFullscreen();

      this.classList.toggle('armada-video--fullscreen', on);
      if (this.iconExpand && this.iconCollapse) {
        this.iconExpand.classList.toggle('hidden', on);
        this.iconCollapse.classList.toggle('hidden', !on);
      }

      const { labelEnter, labelExit } = this.fullscreenButton.dataset;
      const label = on ? labelExit : labelEnter;
      if (label) this.fullscreenButton.setAttribute('aria-label', label);
      this.fullscreenButton.setAttribute('aria-pressed', on ? 'true' : 'false');

      // Entering or leaving is a deliberate act; show the controls again rather
      // than leaving the shopper looking at a bare frame.
      if (this.state === 'playing') this.resetHideTimer();
    }

    /* Auto-hide controls
       ====================================================================== */

    toggleControlsVisibility() {
      if (this.classList.contains('armada-video--controls-hidden')) {
        this.resetHideTimer();
      } else {
        this.classList.add('armada-video--controls-hidden');
        this.clearHideTimer();
      }
    }

    startHideTimer() {
      this.clearHideTimer();
      if (this.state !== 'playing') return;
      this.hideTimer = setTimeout(() => {
        this.classList.add('armada-video--controls-hidden');
      }, 3000);
    }

    resetHideTimer() {
      this.classList.remove('armada-video--controls-hidden');
      this.startHideTimer();
    }

    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    }
  }

  customElements.define('armada-video', ArmadaVideo);
}

/**
 * Variant reactivity (PDP).
 *
 * Lives outside the custom element on purpose: when the selected variant has no
 * video the section renders nothing at all, so there would be no <armada-video>
 * left to listen for the switch that brings it back. The section instead emits a
 * JSON payload — a variant id → PIM video URL map — whenever at least one
 * variant carries a video.
 *
 * The map holds only the URL, used as a signature. Anything that changes it
 * (different video, video → none, none → video) triggers a re-render through the
 * Section Rendering API, so the section's precedence rules — PIM video, then the
 * theme-editor content, then hide — stay in Liquid instead of being reimplemented
 * here and drifting. A matching signature costs nothing: every size of one ski
 * shares a single PIM video, which is the common case and never hits the network.
 */
(function () {
  // One live subscription per section. The theme editor tears the section down
  // and rebuilds it on every setting change, so without this a design-mode
  // session would stack a new listener — and a redundant fetch — per reload.
  const subscriptions = {};

  function bind(script) {
    if (script.dataset.variantSyncBound === 'true') return;
    script.dataset.variantSyncBound = 'true';

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    let config;
    try {
      config = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }
    if (!config || !config.map || !config.url) return;

    const sectionId = script.getAttribute('data-armada-video-variants');
    if (subscriptions[sectionId]) subscriptions[sectionId]();

    let current = config.current || '';
    let token = 0;

    subscriptions[sectionId] = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      // Resolved per event rather than cached: a design-mode reload replaces the
      // wrapper, and a cached reference would then write into a detached node.
      const wrapper = document.getElementById('shopify-section-' + sectionId);
      if (!wrapper) return;

      const variant = event && event.data && event.data.variant;
      if (!variant) return;

      const next = config.map[variant.id] || '';
      if (next === current) return;
      current = next;

      // Guards against a slower earlier request landing after a faster later one
      // and restoring the wrong variant's video.
      const ticket = ++token;

      fetch(config.url + '?variant=' + encodeURIComponent(variant.id) + '&section_id=' + encodeURIComponent(sectionId))
        .then((response) => response.text())
        .then((text) => {
          if (ticket !== token) return;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fresh = doc.getElementById('shopify-section-' + sectionId);
          wrapper.innerHTML = fresh ? fresh.innerHTML : '';

          // <armada-video> upgrades itself on insertion. The payload script came
          // along with the markup, but this subscription is still live and holds
          // the same map, so flag it rather than binding a second listener.
          const refreshed = wrapper.querySelector('[data-armada-video-variants]');
          if (refreshed) refreshed.dataset.variantSyncBound = 'true';
        })
        .catch(() => {});
    });
  }

  function init() {
    document.querySelectorAll('script[data-armada-video-variants]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section from scratch, replacing the payload.
  document.addEventListener('shopify:section:load', init);
})();
